// ChallengePlatformPage — Step 3: choose trading platform
// iOS HIG: inset-grouped list of platforms with colored icon + checkmark

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChallengeLayout, IOSCheck, IOSList, IOSPrimaryButton, IOSRow, stepPath } from './ChallengeLayout';
import { PLATFORM_OPTIONS, getSizeOption } from './data';
import { loadDraft, setPlatform, type PlatformKey, type SizeKey, type TierName } from './store';
import { BrandLogo } from '../../../components/Brand';

export function ChallengePlatformPage() {
  const nav = useNavigate();
  const draft = loadDraft();
  const tier: TierName | undefined = draft.tier;
  const size: SizeKey | undefined = draft.size;
  const [selected, setSelected] = useState<PlatformKey | null>(draft.platform || null);

  useEffect(() => {
    if (!tier) nav('/portal/challenge/tier', { replace: true });
    else if (!size) nav('/portal/challenge/size', { replace: true });
  }, [tier, size, nav]);

  if (!tier || !size) return null;
  const sizeOpt = getSizeOption(tier, size);

  function onContinue() {
    if (!selected) return;
    setPlatform(selected);
    nav(stepPath('checkout'));
  }

  return (
    <ChallengeLayout
      current="platform"
      subtitle={sizeOpt ? `$${sizeOpt.price} challenge · ${tier} ${size}K` : undefined}
    >
      <div>
        <div className="px-1 pb-2 text-[13px] font-normal uppercase tracking-wide text-[#8E8E93]">Trading platform</div>
        <IOSList>
          {PLATFORM_OPTIONS.map((p, i) => {
            const isSel = selected === p.key;
            return (
              <IOSRow
                key={p.key}
                testId={`platform-card-${p.key.toLowerCase()}`}
                selected={isSel}
                onClick={() => setSelected(p.key)}
                showDivider={i < PLATFORM_OPTIONS.length - 1}
                leading={<BrandLogo name={p.key} size={28} />}
                title={
                  <div className="flex items-center gap-2">
                    <span className="text-[17px]">{p.displayName}</span>
                    {p.badge && (
                      <span className="rounded-full bg-[#FF9500] px-2 py-0.5 text-[11px] font-semibold uppercase text-white">{p.badge}</span>
                    )}
                  </div>
                }
                subtitle={p.description}
                trailing={<IOSCheck selected={isSel} />}
              />
            );
          })}
        </IOSList>
        <p className="mt-3 px-1 text-[13px] text-[#8E8E93]">
          Platform license is included for the duration of your active challenge.
        </p>
      </div>

      <div className="mt-8">
        <IOSPrimaryButton onClick={onContinue} disabled={!selected}>
          Continue to checkout
        </IOSPrimaryButton>
      </div>
    </ChallengeLayout>
  );
}

function PlatformGlyph({ name }: { name: PlatformKey }) {
  if (name === 'Deepchart' || name === 'Deepdom') {
    return (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 17l4-4 3 3 7-7" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="3" y="3" width="18" height="18" rx="2" />
      </svg>
    );
  }
  if (name === 'ATAS') {
    return (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" />
        <line x1="9" y1="3" x2="9" y2="21" /><line x1="15" y1="3" x2="15" y2="21" />
      </svg>
    );
  }
  if (name === 'Quantower') {
    return (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 21h18" strokeLinecap="round" />
        <rect x="5" y="11" width="3" height="7" />
        <rect x="10.5" y="6" width="3" height="12" />
        <rect x="16" y="13" width="3" height="5" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 5h18M3 9h18M3 13h18M3 17h18M3 21h18" strokeLinecap="round" />
    </svg>
  );
}
